import { Identify, identify } from "@amplitude/analytics-browser"

interface LocationData {
  city: string
  state: string
  country: string
}

type LocationProvider = {
  getGeolocationData: () => Promise<LocationData | undefined>
}

declare global {
  interface Window {
    OneTrust: LocationProvider | undefined
  }
}

export type LocationService = Readonly<{
  getLocation(): Promise<LocationData>
}>

const RESOLUTION_TIMEOUT = 2000
const RESOLUTION_INTERVAL = 100
const waitForOneTrust = async (): Promise<LocationProvider> => {
  return new Promise<LocationProvider>((resolve, reject) => {
    const startTime = Date.now()
    let timeout: ReturnType<typeof setTimeout> | undefined

    const checkPresence = () => {
      if (window.OneTrust) {
        if (timeout) clearTimeout(timeout)
        resolve(window.OneTrust)
        return
      }

      const elapsed = Date.now() - startTime
      if (elapsed >= RESOLUTION_TIMEOUT) {
        reject(new Error('OneTrust not available'))
        return
      }

      timeout = setTimeout(
        checkPresence,
        Math.min(RESOLUTION_INTERVAL, RESOLUTION_TIMEOUT - elapsed)
      )
    }

    checkPresence()
  })
}

export const locationService: LocationService = {
  getLocation: async (): Promise<LocationData> => {
    try {
      let locationData: Partial<LocationData> = {}

      if (!locationData.state) {
        // wait for OneTrust  to load
        await waitForOneTrust()

        const oneTrustLocationData = await window.OneTrust?.getGeolocationData()

        if (!oneTrustLocationData) {
          throw new Error('OneTrust location not available')
        }

        const identifyObj = new Identify()
        Object.entries(oneTrustLocationData)
          .filter(([_, value]) => value !== undefined)
          .forEach(([key, value]) => identifyObj.set(key, value))
        identify(identifyObj)

        Object.assign(locationData, oneTrustLocationData)
      }

      return locationData as LocationData
    } catch (error) {
      console.error('Error getting location: ', error)
      return {
        city: '',
        country: '',
        state: '',
      }
    }
  }
}
